import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logoSvg from '../../assets/svg/logo.svg';
import PropTypes from 'prop-types';
import queryPayment from '../../services/payment/queryPayment';
import queryUsers from '../../services/users/queryUsers';
import queryPaymentMethods from '../../services/payment/queryPaymentMethods';
// import { paymentBrandBadgeClassName, paymentBrandLabels } from '../Profile/user-card/constants';
import useAuthContext from '../../hooks/useAuthContext';
import { classNames, formatToCurrency, getCurrencySymbol } from '../../utils/common';
import PhoneIcon from '../Common/Icons/PhoneIcon';
import { EnvelopeIcon, StarIcon } from '@heroicons/react/24/solid';

const ConfirmRecharge = ({
  recharge,
  checkoutData,
  savedContacts = [],
  // onClose,
  onSuccess,
  onProceedToPayment,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [usePoints, setUsePoints] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);

  // Hooks
  const { data: userPointsData } = queryUsers().userPoints();
  const { mutate: rechargeDo, isLoading: isProcessing, isSuccess } = queryPayment().rechargeDo();
  const { data: savedPaymentMethods } = queryPaymentMethods().list();

  const contacts = recharge?.contacts || [];
  const isMovil = !!contacts[0]?.phone;
  const currency = checkoutData?.currency || recharge?.currency || 'eur';
  const currencySymbol = getCurrencySymbol(currency);
  const totalPoints = (recharge?.cost_points || 0) * contacts.length;
  const availablePoints = Number(userPointsData?.points ?? user?.point ?? 0);
  const canUsePoints = recharge?.cost_points > 0 &&
    availablePoints > 0 &&
    totalPoints <= availablePoints;

  // Product info
  const productName = checkoutData?.rechargeable_object?.name || '';
  const productDescription = checkoutData?.rechargeable_object?.description || '';
  const productLabel = productDescription
    ? `${productName} · ${productDescription}`
    : productName;

  // Points earned per recharge
  const pointsEarned = checkoutData?.total_assign_points
    ? checkoutData.total_assign_points * contacts.length
    : 0;

  // Price per contact in the payment currency (EUR/USD)
  const pricePerContact = checkoutData?.total_amount_pay && contacts.length
    ? checkoutData.total_amount_pay / contacts.length
    : 0;

  // CUP value from product name (e.g. "600 CUP" → 600)
  const cupMatch = productName.match(/^([\d.]+)\s*CUP/i);
  const pricePerContactCup = cupMatch ? parseFloat(cupMatch[1]) : null;
  const totalCup = pricePerContactCup ? pricePerContactCup * contacts.length : null;

  // Currency label for "cambio en X"
  const currencyLabel = currency === 'eur' ? 'euros' : 'dólares';

  // Handle success
  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
    }
  }, [isSuccess]);

  // effect to set default payment method
  useEffect(() => {
    if (!savedPaymentMethods?.length || selectedPaymentMethodId) return;
    const defaultMethod = savedPaymentMethods.find((method) => method.isDefault);
    if (!defaultMethod) return
    setSelectedPaymentMethodId(defaultMethod?.id);
  }, [savedPaymentMethods, selectedPaymentMethodId]);

  // if not can use points, go to payment (skip if payment already succeeded)
  useEffect(() => {
    if (!canUsePoints && !isSuccess) {
      onProceedToPayment?.();
    }
  }, [canUsePoints]);

  // Check if a contact is new (not in saved contacts)
  const isNewContact = (contact) => {
    if (isMovil) {
      return !savedContacts?.some(
        (sc) => sc.phone && sc.phone.replace(/^\+53/, '') === contact.phone?.replace(/^\+53/, ''),
      );
    }
    return !savedContacts?.some(
      (sc) => sc.email && sc.email.toLowerCase() === contact.email?.toLowerCase(),
    );
  };

  const handleConfirm = () => {
    if (usePoints) {
      rechargeDo({ id: checkoutData?.id, pay_method: 'point' });
    } else if (selectedPaymentMethodId) {
      rechargeDo({ id: checkoutData?.id, pay_method: 'stripe', payment_method_id: selectedPaymentMethodId });
    } else {
      onProceedToPayment?.();
    }
  };

  const formatPhone = (phone) => {
    const clean = phone?.replace(/^\+53/, '') || '';
    if (clean.length === 8) {
      return `+53 ${clean.slice(0, 2)} ${clean.slice(2, 5)} ${clean.slice(5)}`;
    }
    return `+53 ${clean}`;
  };

  return (
    <div className="flex flex-col w-full select-none">
      {/* ─── Title ─── */}
      <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-5">
        {t('confirmRecharge.title')}
      </h2>

      {/* ═══════════════════════════════════════════════════════
          SECTION: Tu recarga
         ═══════════════════════════════════════════════════════ */}
      <div className="mb-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          {t('confirmRecharge.yourRecharge')}
        </h3>

        {/* Contact list */}
        <div className="flex flex-col gap-3">
          {contacts.map((contact, i) => {
            const isNew = isNewContact(contact);
            const displayValue = isMovil ? formatPhone(contact.phone) : contact.email;
            const displayName = contact.name || (isNew ? t('confirmRecharge.newContact') : displayValue);

            return (
              <div key={i} className="flex items-center gap-3">
                <div
                  className={classNames(
                    'flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0',
                    isNew ? 'bg-amber-50 border border-amber-200' : 'bg-gray-100',
                  )}
                >
                  {isMovil ? (
                    <PhoneIcon w="14px" h="20px" color={isNew ? '#D97706' : '#6B7280'} />
                  ) : (
                    <EnvelopeIcon className={classNames('w-4 h-4', isNew ? 'text-amber-600' : 'text-gray-500')} />
                  )}
                </div>
                <div className="flex flex-col min-w-0">
                  <span
                    className={classNames(
                      'text-sm font-medium truncate',
                      isNew ? 'text-amber-600 italic' : 'text-gray-900',
                    )}
                  >
                    {displayName}
                  </span>
                  <span className="text-xs text-gray-400 truncate">{displayValue}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Product / Offer badge */}
        {productLabel && (
          <div className="flex items-center gap-2 mt-4">
            <span className="text-xs text-gray-500">{t('confirmRecharge.offer')}:</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
              {productLabel}
            </span>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════
          SECTION: Detalles de pago (solo si hay puntos disponibles)
         ═══════════════════════════════════════════════════════ */}
      {canUsePoints && (
        <>
          <hr className="border-gray-200" />
          <div className="py-5">
            <button
              type="button"
              onClick={() => { setUsePoints(!usePoints); setSelectedPaymentMethodId(null); }}
              className="flex items-center justify-between w-full py-1"
            >
              <div className="flex flex-col items-start">
                <span className="text-sm text-gray-900 font-medium">
                  {t('confirmRecharge.usePoints')}
                </span>
                <span className="text-xs text-primary font-medium">
                  {t('confirmRecharge.pointsAvailable', {
                    points: Number(availablePoints).toFixed(0),
                  })}
                </span>
              </div>

              {/* Custom toggle */}
              <div
                className={classNames(
                  'relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0',
                  usePoints ? 'bg-primary' : 'bg-gray-300',
                )}
              >
                <div
                  className={classNames(
                    'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200',
                    usePoints ? 'translate-x-[22px]' : 'translate-x-0.5',
                  )}
                />
              </div>
            </button>
          </div>
          <hr className="border-gray-200" />
        </>
      )}

      {!canUsePoints && <hr className="border-gray-200" />}

      {/* ═══════════════════════════════════════════════════════
          SECTION: Métodos de pago guardados (Esta seccion no se usa, porque lo manejara directamente stripe, quien dispara el payment intention)
         ═══════════════════════════════════════════════════════ */}
      {/* {savedPaymentMethods?.length > 0 && !usePoints && (
        <>
          <div className="py-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {t('confirmRecharge.savedCards', 'Pagar con tarjeta guardada')}
            </h3>
            <div className="flex flex-col gap-2">
              {savedPaymentMethods.map((pm) => (
                <button
                  key={pm.id}
                  type="button"
                  onClick={() => setSelectedPaymentMethodId(
                    selectedPaymentMethodId === pm.id ? null : pm.id
                  )}
                  className={classNames(
                    'flex items-center justify-between rounded-xl border px-3 py-2.5 w-full text-left transition-colors',
                    selectedPaymentMethodId === pm.id
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 bg-white hover:border-gray-300',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={classNames(
                        'rounded px-2 py-1 text-[10px] font-bold',
                        paymentBrandBadgeClassName[pm.brand] || 'bg-slate-800 text-white',
                      )}
                    >
                      {paymentBrandLabels[pm.brand] || pm.brand?.toUpperCase() || 'CARD'}
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-gray-800">•••• {pm.last4}</p>
                      <p className="text-[10px] text-gray-400">
                        {String(pm.expMonth).padStart(2, '0')}/{String(pm.expYear).slice(-2)}
                      </p>
                    </div>
                  </div>
                  <span
                    className={classNames(
                      'flex h-5 w-5 items-center justify-center rounded-full border flex-shrink-0',
                      selectedPaymentMethodId === pm.id ? 'border-primary' : 'border-gray-300',
                    )}
                  >
                    <span
                      className={classNames(
                        'h-3 w-3 rounded-full',
                        selectedPaymentMethodId === pm.id ? 'bg-primary' : 'bg-transparent',
                      )}
                    />
                  </span>
                </button>
              ))}
            </div>
          </div>
          <hr className="border-gray-200" />
        </>
      )}*/}

      {/* ═══════════════════════════════════════════════════════
          SECTION: Desglose
         ═══════════════════════════════════════════════════════ */}
      <div className="pt-5 pb-4 mt-3 bg-primary/20 px-4 rounded-xl">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          {t('confirmRecharge.breakdown')}
        </h3>

        <div className="flex flex-col gap-2.5">
          {contacts.map((contact, i) => {
            const displayValue = isMovil ? formatPhone(contact.phone) : contact.email;
            const displayName = contact.name ? ` · ${contact.name}` : '';

            return (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-gray-500 truncate mr-4">
                  {displayValue}{displayName}
                </span>
                <span className="text-sm text-gray-700 font-normal whitespace-nowrap">
                  {pricePerContactCup != null
                    ? `${pricePerContactCup.toLocaleString('es-ES')} CUP`
                    : `${formatToCurrency(pricePerContact)} ${currencySymbol}`}
                </span>
              </div>
            );
          })}

          {/* Total */}
          <div className="flex items-center justify-between pt-2.5 border-t border-primary/30 mt-1">
            <span className="text-base font-bold text-gray-900">
              {t('confirmRecharge.total')}
            </span>
            <span className="text-base font-bold text-gray-900">
              {totalCup != null
                ? `${totalCup.toLocaleString('es-ES')} CUP`
                : `${formatToCurrency(checkoutData?.total_amount_pay)} ${currencySymbol}`}
            </span>
          </div>

          {/* Cambio en euros/dólares */}
          {totalCup != null && checkoutData?.total_amount_pay > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">
                {t('confirmRecharge.exchangeRate', { currency: currencyLabel })}
              </span>
              <span className="text-sm text-gray-500">
                {formatToCurrency(checkoutData.total_amount_pay)} {currencySymbol}
              </span>
            </div>
          )}
        </div>

      </div>

      {/* Banner: Points earned — outside breakdown, between desglose and button */}
      {pointsEarned > 0 && (
        <div className="flex items-center gap-3 pl-4 pr-3 py-3 bg-[#EAF1F1] border-l-4 border-[#187672] rounded-r-xl mt-3">
          <StarIcon className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-gray-700 font-medium flex-1">
            {t('confirmRecharge.earnPoints', { points: pointsEarned })}
          </span>
          <img src={logoSvg} alt="logo" className="w-8 h-8 flex-shrink-0 opacity-80" />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          BUTTON: Confirmar recarga
         ═══════════════════════════════════════════════════════ */}
      <div className="pt-2 pb-1">
        <button
          type="button"
          disabled={isProcessing || !checkoutData?.id}
          className={classNames(
            'w-full py-3.5 rounded-full text-white font-semibold text-base transition-all',
            isProcessing || !checkoutData?.id
              ? 'bg-gray-300 cursor-not-allowed'
              : 'bg-primary hover:bg-primary/90 active:bg-primary/80 hover:shadow-lg',
          )}
          onClick={handleConfirm}
        >
          {isProcessing
            ? t('confirmRecharge.processing')
            : !checkoutData?.id
              ? t('confirmRecharge.preparing', 'Preparando...')
              : (usePoints)
                ? t('confirmRecharge.confirmButton')
                : t('confirmRecharge.proceedToPayment', 'Ir a pagar')}
        </button>
      </div>
    </div>
  );
};

ConfirmRecharge.propTypes = {
  recharge: PropTypes.shape({
    contacts: PropTypes.array,
    currency: PropTypes.string,
    cost_points: PropTypes.number,
    rechargeable_id: PropTypes.number,
    rechargeable_type: PropTypes.string,
  }),
  checkoutData: PropTypes.shape({
    id: PropTypes.number,
    total_amount_pay: PropTypes.number,
    total_assign_points: PropTypes.number,
    currency: PropTypes.string,
    rechargeable_object: PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
      assign_points: PropTypes.number,
    }),
  }),
  savedContacts: PropTypes.array,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  onProceedToPayment: PropTypes.func,
};

export default ConfirmRecharge;
